import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import MemberOnboardingModule from "components/member/MemberOnboardingModule";
import PersonalizedTasks from "components/tasks/PersonalizedTasks";
import WelcomeHeader from "components/member/home/WelcomeHeader";
import TaskSummary from "components/member/home/TaskSummary";
import QuickLinks from "components/member/home/QuickLinks";
import { ActivityFeed } from "components/member/home/ActivityFeed";
import TaskList from "components/member/home/TaskList";
import AdvisorInfo from "components/member/home/AdvisorInfo";
import CalendarTasks from "components/member/home/CalendarTasks";
import AddTask from "components/member/home/AddTask";
import FlexBox from "components/common/FlexBox";
import UpcomingVideoCalls from "components/common/UpcomingVideoCalls";
import NoticeBanner from "components/common/NoticeBanner";
import FeatureSuggestionsModule from "components/member/FeatureSuggestionsModule";
import GatedComponent, {
  FEATURE_FLAGS,
} from "components/helpers/GatedComponent";
import { HomeLayoutProps } from "./types";

export default ({
  data,
  loading,
  currentUser,
  integrationCount,
  integrationsLoading,
  calendarTwoWaySyncUpsell,
  isMobile,
  isSystemAdvisor,
  isDeactivated,
  productPrompts,
  handleDismissReferralBanner,
  handleReferralBannerLinkClick,
  handleDismissFeatureSuggestions,
  handleDismissLightningBanner,
  handleLightningBannerClick,
  handleDismissReviewPrompt,
  openReview,
}: HomeLayoutProps) => {
  return (
    <Box sx={{ maxWidth: "900px" }}>
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          transform: "translateX(calc(-50% + 25px))",
          width: "100%",
          backgroundImage: "url('../../assets/images/sparkles.png')",
          backgroundPosition: "top right",
          backgroundRepeat: "no-repeat",
          backgroundSize: "auto",
          backgroundColor: "#D9F0EB",
          zIndex: 1,
          margin: "0px -24px",
          marginTop: "-82px",
          padding: "0px 24px",
          paddingTop: "82px",
          height: "100vh",
        }}
      ></Box>
      <FlexBox
        direction="column"
        gap="16px"
        sx={{ position: "relative", zIndex: 3 }}
      >
        <WelcomeHeader user={currentUser} />
        <UpcomingVideoCalls family={data?.family!} />
        {!isDeactivated &&
          productPrompts?.referralPromoDismissed !== "dismissed" && (
            <NoticeBanner
              title="🎉 Earn $50 for Every Friend You Refer!"
              subtext={
                <Typography variant="body">
                  Love Faye? Share the love! Give friends 20% off their first
                  month, and get a $50 credit when they get started with an
                  Advisor. There's no limit to how much you can earn!{" "}
                  <Link
                    component={RouterLink}
                    to="/invite"
                    fontWeight={700}
                    onClick={handleReferralBannerLinkClick}
                  >
                    Share your link here!
                  </Link>
                </Typography>
              }
              dismissible={true}
              onDismiss={handleDismissReferralBanner}
            />
          )}
        <GatedComponent
          featureName={FEATURE_FLAGS.DELEGATE_PHONE_CALL.value}
          includeAdmin={false}
          returnNull={true}
        >
          {productPrompts?.lightningTaskBannerDismissed !== "dismissed" && (
            <NoticeBanner
              title="⚡ Need to fire off a quick task?"
              subtext="Create a lightning task for a quick call or other fast action. We will send it to our queue for the first available advisor to pick up."
              buttonText="Try now!"
              dismissible={true}
              onDismiss={handleDismissLightningBanner}
              onClick={handleLightningBannerClick}
            />
          )}
        </GatedComponent>
      </FlexBox>

      <Box
        sx={{
          boxShadow: "0px -4px 27px 0px rgba(0, 0, 0, 0.10)",
          borderRadius: "32px 32px 0px 0px",
          margin: "-24px",
          marginTop: "24px",
          padding: isMobile ? "32px 24px" : "24px",
          backgroundColor: "#FFF",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "40px" : "24px",
          position: "relative",
          zIndex: 10,
        }}
      >
        <MemberOnboardingModule
          member={data?.member!}
          family={data?.family!}
          showGoogleCalendarSyncUpsell={calendarTwoWaySyncUpsell}
        />
        {!isDeactivated && (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <AdvisorInfo
                advisor={data?.primaryAdvisor!}
                isDeactivated={isDeactivated}
                openReview={openReview}
                familyCreatedAtSec={data?.family?.createdAtSec}
                productPrompts={productPrompts}
                onDismissReview={handleDismissReviewPrompt}
              />
              <AddTask family={data?.family!} />
              {!isSystemAdvisor && (
                <TaskSummary planUtilization={data?.planUtilization} />
              )}
            </Box>
            <ActivityFeed family={data?.family} />
          </>
        )}
        {productPrompts?.featureSuggestionsDismissed !== "dismissed" && (
          <FeatureSuggestionsModule
            onDismiss={handleDismissFeatureSuggestions}
          />
        )}
        <TaskList tasks={data?.activeTasks || []} loading={loading} />
        <CalendarTasks
          family={data?.family!}
          integrationCount={integrationCount}
          forceLoading={integrationsLoading}
        />
        <PersonalizedTasks
          family={data?.family!}
          integrationCount={integrationCount}
          forceLoading={integrationsLoading}
        />
        <QuickLinks />
      </Box>
    </Box>
  );
};
